import { Injectable } from '@angular/core';
import {
	ActivatedRouteSnapshot,
	BaseRouteReuseStrategy,
} from '@angular/router';

/**
 * Route reuse strategy that preserves the ClientsReportPage component when
 * navigating between edit/preview (and with/without `/page/:pageId`) for the
 * same report.
 *
 * Without this, URL canonicalization after load (`/edit` → `/edit/page/:id`)
 * destroys and recreates the page, causing a double `loadReport`.
 *
 * Detach/store/attach/retrieve stay on {@link BaseRouteReuseStrategy} defaults
 * (no detached-route caching).
 */
@Injectable({ providedIn: 'root' })
export class ReportModeReuseStrategy extends BaseRouteReuseStrategy {
	/**
	 * Reuse when staying on the same client report editor surface so mode
	 * toggles and page-id URL updates do not remount the component.
	 */
	shouldReuseRoute(
		future: ActivatedRouteSnapshot,
		curr: ActivatedRouteSnapshot
	): boolean {
		const futureIsEditor = this.isClientsReportEditorRoute(future);
		const currIsEditor = this.isClientsReportEditorRoute(curr);

		if (futureIsEditor && currIsEditor) {
			// Same report (any edit/preview/page combo): keep the instance.
			// Different report/client: force remount even if routeConfig matches.
			return this.isSameReportEditorContext(future, curr);
		}

		return super.shouldReuseRoute(future, curr);
	}

	private isClientsReportEditorRoute(route: ActivatedRouteSnapshot): boolean {
		const path = route.routeConfig?.path ?? '';
		return /reports\/:reportId\/(edit|preview)/.test(path);
	}

	private isSameReportEditorContext(
		future: ActivatedRouteSnapshot,
		curr: ActivatedRouteSnapshot
	): boolean {
		const futureReportId = future.paramMap.get('reportId');
		const currReportId = curr.paramMap.get('reportId');
		const futureClientId = future.paramMap.get('id');
		const currClientId = curr.paramMap.get('id');

		return (
			!!futureReportId &&
			!!currReportId &&
			futureReportId === currReportId &&
			!!futureClientId &&
			!!currClientId &&
			futureClientId === currClientId
		);
	}
}
